import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Building2, Shield, Zap, Brain, Database, Users, CheckCircle, Calendar, Clock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import tulioIcon from './assets/tulio.png'


const EnterprisePage = () => {
  const enterpriseServices = [
    {
      id: 'digital-transformation',
      title: 'Digital Transformation',
      description: 'Modernize legacy systems and processes for the digital age',
      icon: Zap,
      color: 'from-blue-500 to-indigo-600',
      pricing: 'Custom pricing',
      timeline: '3-12 months',
      features: ['Legacy system modernization', 'Cloud migration', 'Process automation', 'Change management']
    },
    {
      id: 'ai-enterprise',
      title: 'Enterprise AI Solutions',
      description: 'Deploy AI at scale across your organization',
      icon: Brain,
      color: 'from-purple-500 to-pink-600',
      pricing: 'Starting $100k',
      timeline: '4-8 months',
      features: ['AI strategy consulting', 'Custom ML models', 'AI governance', 'Enterprise integrations']
    },
    {
      id: 'data-platform',
      title: 'Enterprise Data Platform',
      description: 'Unified data architecture for business intelligence',
      icon: Database,
      color: 'from-green-500 to-teal-600',
      pricing: 'Starting $150k',
      timeline: '6-12 months',
      features: ['Data lake architecture', 'Real-time analytics', 'Data governance', 'Advanced reporting']
    },
    {
      id: 'system-integration',
      title: 'System Integration',
      description: 'Connect and harmonize your enterprise systems',
      icon: Building2,
      color: 'from-orange-500 to-red-600',
      pricing: 'Starting $75k',
      timeline: '3-6 months',
      features: ['API development', 'ERP integration', 'Data synchronization', 'Workflow automation']
    }
  ];

  const enterpriseBenefits = [
    {
      title: 'Proven at Scale',
      description: 'Successfully delivered projects for Fortune 500 companies',
      icon: Building2,
      stats: '50+ Enterprise Clients'
    },
    {
      title: 'Security First',
      description: 'Enterprise-grade security and compliance frameworks',
      icon: Shield,
      stats: '100% Compliance Record'
    },
    {
      title: 'Global Delivery',
      description: '24/7 support with teams across multiple time zones',
      icon: Users,
      stats: '4 Countries, 3 Time Zones'
    }
  ];

  const industryExpertise = [
    {
      name: 'Financial Services',
      projects: '15+',
      specialties: ['Risk Management', 'Compliance Automation', 'Digital Banking', 'Fraud Detection']
    },
    {
      name: 'Manufacturing',
      projects: '20+',
      specialties: ['IoT Integration', 'Predictive Maintenance', 'Supply Chain', 'Quality Control']
    },
    {
      name: 'Healthcare',
      projects: '12+',
      specialties: ['HIPAA Compliance', 'Patient Data', 'Telemedicine', 'Clinical Analytics']
    },
    {
      name: 'Retail & E-commerce',
      projects: '25+',
      specialties: ['Omnichannel', 'Inventory Management', 'Customer Analytics', 'POS Systems']
    }
  ];

  const enterpriseFeatures = [
    {
      category: 'Security & Compliance',
      features: [
        'SOC 2 Type II compliance',
        'ISO 27001 certified processes',
        'GDPR and data privacy compliance',
        'Enterprise-grade security protocols',
        'Regular security audits and assessments'
      ]
    },
    {
      category: 'Scalability & Performance',
      features: [
        'Auto-scaling cloud architecture',
        'High availability (99.9% uptime SLA)',
        'Performance monitoring and optimization',
        'Load balancing and disaster recovery',
        'Multi-region deployment capabilities'
      ]
    },
    {
      category: 'Integration & Support',
      features: [
        'Seamless ERP and CRM integrations',
        'API-first development approach',
        'Dedicated account management',
        '24/7 technical support',
        'Training and knowledge transfer'
      ]
    }
  ];

  const caseStudies = [
    {
      company: 'Global Manufacturing Corp',
      industry: 'Manufacturing',
      challenge: 'Legacy system modernization across 50+ facilities',
      result: '$2M annual savings',
      impact: 'Reduced downtime by 40%',
      timeline: '8 months'
    },
    {
      company: 'International Bank',
      industry: 'Financial Services',
      challenge: 'Digital transformation and regulatory compliance',
      result: '99.99% uptime achieved',
      impact: '50% faster transaction processing',
      timeline: '12 months'
    },
    {
      company: 'Retail Chain',
      industry: 'Retail',
      challenge: 'Omnichannel platform for 200+ stores',
      result: '30% increase in revenue',
      impact: 'Unified customer experience',
      timeline: '6 months'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
             <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            <Navigation />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-100">Enterprise Solutions</Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Enterprise Digital
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Transformation
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Partner with us to modernize your enterprise technology, accelerate innovation, and achieve measurable business outcomes at scale.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Enterprise Consultation
                </Button>
                <Link to="/case-studies">
                  <Button size="lg" variant="outline">
                    View Enterprise Cases
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center space-x-8 text-slate-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Fortune 500 Trusted</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">99.9% Uptime SLA</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Enterprise Scale Impact</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-3xl font-bold">$50M+</div>
                    <div className="text-sm opacity-90">Cost Savings Delivered</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">99.9%</div>
                    <div className="text-sm opacity-90">System Uptime</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">50+</div>
                    <div className="text-sm opacity-90">Enterprise Clients</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">24/7</div>
                    <div className="text-sm opacity-90">Global Support</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border">
                <div className="flex items-center space-x-3">
                  <Shield className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-semibold text-slate-900">SOC 2 Compliant</div>
                    <div className="text-sm text-slate-600">Enterprise Security</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Benefits */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Global Enterprises Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {enterpriseBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                    <p className="text-slate-600 mb-4">{benefit.description}</p>
                    <div className="text-lg font-bold text-blue-600">{benefit.stats}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise Services */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Enterprise Technology Services</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive solutions designed for large-scale enterprise environments and complex business requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {enterpriseServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-blue-200">
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                    <p className="text-slate-600 mb-6">{service.description}</p>
                    
                    <div className="flex justify-between items-center mb-6 text-sm">
                      <div className="text-slate-600">
                        <strong>Investment:</strong> {service.pricing}
                      </div>
                      <div className="flex items-center text-slate-600">
                        <Clock className="w-4 h-4 mr-1" />
                        {service.timeline}
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">
                      Request Consultation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Industry Expertise</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Deep domain knowledge across key industries with proven track records and specialized solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryExpertise.map((industry, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-bold mb-2">{industry.name}</h3>
                    <div className="text-2xl font-bold text-blue-600">{industry.projects}</div>
                    <div className="text-sm text-slate-600">Projects Delivered</div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3 text-sm">Specialties:</h4>
                    <ul className="space-y-2">
                      {industry.specialties.map((specialty, idx) => (
                        <li key={idx} className="text-xs text-slate-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
                          {specialty}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Enterprise-Grade Capabilities</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Built for enterprise scale with security, compliance, and reliability at the core.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {enterpriseFeatures.map((category, index) => (
              <Card key={index} className="h-full">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-6">{category.category}</h3>
                  <ul className="space-y-3">
                    {category.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Enterprise Success Stories</h2>
            <p className="text-xl text-slate-600">Real impact for global organizations</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Badge className="mb-4" variant="secondary">{study.industry}</Badge>
                  <h3 className="text-lg font-bold mb-2">{study.company}</h3>
                  <p className="text-slate-600 text-sm mb-4">{study.challenge}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Key Result:</span>
                      <span className="font-semibold text-blue-600">{study.result}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Impact:</span>
                      <span className="font-semibold text-emerald-600">{study.impact}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm text-slate-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {study.timeline} delivery
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Enterprise?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join global enterprises who trust Tulio Labs to deliver mission-critical technology solutions that drive business growth.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Enterprise Consultation
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                Explore All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          
          <div className="mt-8 flex items-center justify-center space-x-8 text-slate-400">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span className="text-sm">SOC 2 Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4" />
              <span className="text-sm">99.9% Uptime SLA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
             <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>
            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/enterprise" className="hover:text-white transition-colors">Enterprise</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EnterprisePage;